import { useState, type ReactNode } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import {
  RiArrowLeftSLine,
  RiCheckLine,
  RiHome4Line,
  RiFireLine,
  RiStore2Line,
  RiBuilding2Line,
  RiRestaurantLine,
  RiCupLine,
  RiHotelLine,
  RiTruckLine,
  RiBriefcaseLine,
  RiHeartLine,
  RiNotification3Line,
  RiAlarmWarningLine,
  RiMessage2Line,
  RiTeamLine,
  RiPrinterLine,
  RiPhoneLine,
  RiLinkUnlinkM,
} from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { IdentityContext, useUser } from "@utils/store/user/useUser";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import appToast from "@components/Toast";
import { handleAPIRenameContext } from "./API/handleContextAPI";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import ToggleRow from "./components/ui/ToggleRow";
import FnRow from "./components/ui/FnRow";
import DetailRow from "./components/ui/DetailRow";
import BottomSheet from "./components/ui/BottomSheet";

/** 代表顏色選項（切版用 UI 結構，非業務資料） */
interface AccountColorOption {
  name: string;
  value: string;
}

/** 代表圖示選項（切版用 UI 結構，非業務資料） */
interface AccountIconOption {
  id: string;
  label: string;
  icon: ReactNode;
}

const ACCOUNT_COLORS: AccountColorOption[] = [
  { name: "藍", value: "#2563eb" },
  { name: "綠", value: "#10B981" },
  { name: "琥珀", value: "#F59E0B" },
  { name: "紫", value: "#8B5CF6" },
  { name: "紅", value: "#EF4444" },
  { name: "青", value: "#008A93" },
  { name: "粉", value: "#EC4899" },
  { name: "深灰", value: "#475569" },
];

const ACCOUNT_ICONS: AccountIconOption[] = [
  { id: "home", label: "住家", icon: <RiHome4Line size={20} /> },
  { id: "store", label: "店面", icon: <RiStore2Line size={20} /> },
  { id: "building", label: "大樓", icon: <RiBuilding2Line size={20} /> },
  { id: "restaurant", label: "餐廳", icon: <RiRestaurantLine size={20} /> },
  { id: "cup", label: "飲料", icon: <RiCupLine size={20} /> },
  { id: "hotel", label: "旅宿", icon: <RiHotelLine size={20} /> },
  { id: "truck", label: "配送", icon: <RiTruckLine size={20} /> },
  { id: "briefcase", label: "公司", icon: <RiBriefcaseLine size={20} /> },
  { id: "fire", label: "瓦斯", icon: <RiFireLine size={20} /> },
  { id: "heart", label: "其他", icon: <RiHeartLine size={20} /> },
];

/**
 * 瓦斯行帳戶設定頁（切版）。
 * 外觀（顏色 / 圖示）、基本資料、自動化與通知、進階管理、危險區域。
 * 純 UI 切版：欄位來自 router state 的 IdentityContext 或空值，無業務 mock 資料。
 */
export const AccountSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId } = useParams();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;

  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity, setChooseIdentity } = useUser();

  const name = ctx?.context_name || ctx?.label || "";
  const vendor = ctx?.primary_supplier?.supplier_name || "未綁定瓦斯行";

  // 本地 UI 狀態（切版）
  const [nickname, setNickname] = useState<string>(name);
  const [saving, setSaving] = useState<boolean>(false);
  const [accColor, setAccColor] = useState<string>(ACCOUNT_COLORS[0].value);
  const [accIcon, setAccIcon] = useState<string>(ACCOUNT_ICONS[0].id);
  const [autoOrder, setAutoOrder] = useState<boolean>(false);
  const [lowStockAlert, setLowStockAlert] = useState<boolean>(true);
  const [smsNotify, setSmsNotify] = useState<boolean>(false);
  const [unbindOpen, setUnbindOpen] = useState<boolean>(false);

  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });

  /**
   * 儲存帳戶名稱（context_name）。
   * 名稱未變更 → 直接返回不打 API；成功後同步 chooseIdentity 並帶更新後的 context 返回詳情頁。
   */
  const handleSave = async () => {
    if (saving) return;

    const trimmed = nickname.trim();
    if (!trimmed) {
      appToast.error("請輸入帳戶名稱");
      return;
    }
    if (!cusJwtToken || !contextId) {
      appToast.error("請重新登入後再試");
      return;
    }
    // 名稱沒變更 → 不打 API，直接返回
    if (trimmed === (ctx?.context_name ?? "")) {
      back();
      return;
    }

    setSaving(true);
    const result = await handleAPIRenameContext(contextId, trimmed, cusJwtToken);
    setSaving(false);

    if (result.state !== "success") {
      appToast.error(result.error || "更新失敗");
      return;
    }

    const newName = result.data?.context_name ?? trimmed;
    // 同步更新當前選擇身分（若改的就是它），讓切換選單與其他頁立即反映新名稱
    if (chooseIdentity && String(chooseIdentity.context_id) === String(contextId)) {
      setChooseIdentity({ ...chooseIdentity, context_name: newName, label: undefined });
    }
    const updatedCtx: IdentityContext | null = ctx
      ? { ...ctx, context_name: newName, label: undefined }
      : ctx;
    appToast.success("帳戶名稱已更新");
    navigate(`/account/detail/${contextId}`, { state: { context: updatedCtx } });
  };

  const selectedIcon = ACCOUNT_ICONS.find((i) => i.id === accIcon) ?? ACCOUNT_ICONS[0];

  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      <Header>
        <XStack ai="center" onPress={back} cursor="pointer" mb="$3">
          <View mr="$2">
            <RiArrowLeftSLine size={20} color={colors.light} />
          </View>
          <Text color={colors.light} fontWeight="bold" fontSize={16}>
            返回
          </Text>
        </XStack>
        <Text color={colors.light} fontSize={22} fontWeight="800">
          帳戶設定
        </Text>
        <Text color="rgba(255,255,255,0.85)" fontSize={12} mt="$1.5">
          自訂外觀、通知與綁定管理
        </Text>
      </Header>

      <RadiusContentWrapper space="$4">
        {/* 1. 外觀 */}
        <YStack>
          <SectionLabel title="外觀" />
          <ListContainer p="$3.5" gap="$4">
            {/* 預覽列 */}
            <XStack ai="center" gap="$3">
              <View w={56} h={56} br={16} bg={`${accColor}18`} ai="center" jc="center" flexShrink={0}>
                <View ai="center" jc="center" style={{ color: accColor }}>
                  {selectedIcon.icon}
                </View>
              </View>
              <YStack f={1} miw={0}>
                <Text fontSize={16} fontWeight="700" color={colors.text} numberOfLines={1}>
                  {nickname || "帳戶名稱"}
                </Text>
                <Text fontSize={12} color={colors.sub} mt="$1" numberOfLines={1}>
                  {vendor}
                </Text>
              </YStack>
            </XStack>

            {/* 代表顏色 */}
            <YStack gap="$2.5">
              <Text fontSize={12} fontWeight="600" color={colors.sub}>
                代表顏色
              </Text>
              <XStack fw="wrap" gap="$3">
                {ACCOUNT_COLORS.map((c) => {
                  const active = c.value === accColor;
                  return (
                    <View
                      key={c.value}
                      w={36}
                      h={36}
                      br={18}
                      bg={c.value}
                      ai="center"
                      jc="center"
                      cursor="pointer"
                      bw={active ? 3 : 0}
                      bc={colors.light}
                      onPress={() => setAccColor(c.value)}
                      style={active ? { boxShadow: `0 0 0 2px ${c.value}` } : undefined}>
                      {active && <RiCheckLine size={18} color={colors.light} />}
                    </View>
                  );
                })}
              </XStack>
            </YStack>

            {/* 代表圖示 */}
            <YStack gap="$2.5">
              <Text fontSize={12} fontWeight="600" color={colors.sub}>
                代表圖示
              </Text>
              <XStack fw="wrap" gap="$2.5">
                {ACCOUNT_ICONS.map((opt) => {
                  const active = opt.id === accIcon;
                  return (
                    <YStack
                      key={opt.id}
                      w={56}
                      ai="center"
                      gap="$1"
                      cursor="pointer"
                      onPress={() => setAccIcon(opt.id)}>
                      <View
                        w={44}
                        h={44}
                        br={12}
                        ai="center"
                        jc="center"
                        bg={active ? `${accColor}18` : colors.bg}
                        bw={active ? 2 : 1}
                        bc={active ? accColor : colors.border}>
                        <View ai="center" jc="center" style={{ color: active ? accColor : colors.sub }}>
                          {opt.icon}
                        </View>
                      </View>
                      <Text fontSize={10} color={active ? colors.text : colors.hint} fontWeight={active ? "600" : "400"}>
                        {opt.label}
                      </Text>
                    </YStack>
                  );
                })}
              </XStack>
            </YStack>
          </ListContainer>
        </YStack>

        {/* 2. 基本資料 */}
        <YStack>
          <SectionLabel title="基本資料" />
          <ListContainer>
            <XStack ai="center" jc="space-between" gap="$3" px="$4" py="$3" bbw={1} bbc={colors.borderLight}>
              <Text fontSize={12} color={colors.sub} flexShrink={0}>
                帳戶名稱
              </Text>
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="輸入帳戶名稱"
                style={{
                  flex: 1,
                  minWidth: 0,
                  textAlign: "right",
                  fontSize: 15,
                  fontWeight: 600,
                  color: colors.text,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                }}
              />
            </XStack>
            <DetailRow
              label="瓦斯行"
              customValue={
                <XStack ai="center" gap="$2">
                  <Text fontSize={13} fontWeight="600" color={colors.text} ta="right">
                    {vendor}
                  </Text>
                  <View br={6} px="$2" py="$0.5" bg={colors.bg} bw={1} bc={colors.border}>
                    <Text fontSize={10} color={colors.hint} fontWeight="600">
                      不可修改
                    </Text>
                  </View>
                </XStack>
              }
            />
            <DetailRow label="客戶編號" value="—" mono isLast />
          </ListContainer>
        </YStack>

        {/* 3. 自動化與通知 */}
        <YStack>
          <SectionLabel title="自動化與通知" />
          <ListContainer>
            <ToggleRow
              icon={<RiFireLine size={15} color={colors.primary} />}
              iconColor={colors.primary}
              label="低存量自動叫桶"
              sub="存量偏低時自動建立訂單"
              value={autoOrder}
              onChange={setAutoOrder}
            />
            <ToggleRow
              icon={<RiAlarmWarningLine size={15} color={colors.amber} />}
              iconColor={colors.amber}
              label="低存量提醒"
              sub="瓦斯快用完時推播提醒"
              accentColor={colors.amber}
              value={lowStockAlert}
              onChange={setLowStockAlert}
            />
            <ToggleRow
              icon={<RiMessage2Line size={15} color={colors.green} />}
              iconColor={colors.green}
              label="簡訊通知"
              sub="訂單狀態以簡訊通知"
              accentColor={colors.green}
              value={smsNotify}
              onChange={setSmsNotify}
              isLast
            />
          </ListContainer>
        </YStack>

        {/* 4. 進階管理 */}
        <YStack>
          <SectionLabel title="進階管理" />
          <ListContainer>
            <FnRow
              icon={<RiTeamLine size={15} color={colors.primary} />}
              iconColor={colors.primary}
              label="成員權限"
              sub="管理可使用此帳戶的成員"
              onPress={() => navigate(`/account/detail/${contextId}/members`, { state: { context: ctx } })}
            />
            <FnRow
              icon={<RiPrinterLine size={15} color={colors.purple} />}
              iconColor={colors.purple}
              label="可印出帳單與發票"
              sub="設定帳單與發票列印偏好"
            />
            <FnRow
              icon={<RiPhoneLine size={15} color={colors.green} />}
              iconColor={colors.green}
              label="聯絡瓦斯行"
              sub={`與 ${vendor} 聯繫`}
              isLast
            />
          </ListContainer>
        </YStack>

        {/* 5. 危險區域 */}
        <YStack>
          <SectionLabel title="危險區域" right={<Text fontSize={10} color={colors.danger}>不可復原</Text>} />
          <ListContainer bw={1} bc={colors.dangerLight}>
            <FnRow
              icon={<RiLinkUnlinkM size={15} color={colors.danger} />}
              iconColor={colors.danger}
              label="解除帳戶綁定"
              sub="解除後將無法存取此帳戶資料"
              danger
              isLast
              onPress={() => setUnbindOpen(true)}
            />
          </ListContainer>
        </YStack>

        {/* 6. 儲存變更 */}
        <XStack
          className="primary-bg-gradient"
          ai="center"
          jc="center"
          br={16}
          py="$4"
          cursor={saving ? "not-allowed" : "pointer"}
          opacity={saving ? 0.6 : 1}
          onPress={handleSave}>
          <Text fontSize={15} fontWeight="700" color={colors.light}>
            {saving ? "儲存中…" : "儲存變更"}
          </Text>
        </XStack>
      </RadiusContentWrapper>

      {/* 解除綁定確認 */}
      <BottomSheet open={unbindOpen} onClose={() => setUnbindOpen(false)}>
        <YStack px="$4" pb="$5" gap="$3.5">
          <View w={48} h={48} br={24} bg={colors.bgRed} ai="center" jc="center" style={{ margin: "0 auto" }}>
            <RiLinkUnlinkM size={22} color={colors.danger} />
          </View>
          <YStack gap="$1.5" ai="center">
            <Text fontSize={17} fontWeight="800" color={colors.text}>
              確定要解除綁定？
            </Text>
            <Text fontSize={12} color={colors.sub} ta="center" lineHeight={18}>
              解除「{nickname || name || "此帳戶"}」與 {vendor} 的綁定後，將無法再存取此帳戶的訂單與資料。
            </Text>
          </YStack>
          <YStack gap="$2.5" mt="$1">
            <XStack
              ai="center"
              jc="center"
              br={14}
              py="$3.5"
              bg={colors.danger}
              cursor="pointer"
              onPress={() => setUnbindOpen(false)}>
              <Text fontSize={14} fontWeight="700" color={colors.light}>
                確定解除
              </Text>
            </XStack>
            <XStack
              ai="center"
              jc="center"
              br={14}
              py="$3.5"
              bg={colors.bg}
              bw={1}
              bc={colors.border}
              cursor="pointer"
              onPress={() => setUnbindOpen(false)}>
              <Text fontSize={14} fontWeight="600" color={colors.sub}>
                取消
              </Text>
            </XStack>
          </YStack>
        </YStack>
      </BottomSheet>
    </YStack>
  );
};

export default AccountSettings;
